import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import React, {
    ChangeEvent,
    MouseEvent,
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import styled from 'styled-components';
import DeleteQuestionnaireButton from '../../assets/delete_button.svg';
import AuthStore from '../../store/AuthStore';
import Api from '../../utils/Api';
import { BranchEntity } from '../@types/mysqlEntities';
import { BRANCH } from '../const/MerchantProperties';
import { colors } from '../const/Styles';
import { MerchantTabContext } from '../../contexts/MerchantTabContext';

const useStyles = makeStyles(() =>
    createStyles({
        lessonName: {
            width: '100%',
            maxWidth: 664,
            boxSizing: 'border-box',
            borderRadius: 0,
            margin: '10px auto 0',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            padding: '10px 12px',
        },
        formTitle: {
            flexShrink: 0,
            fontWeight: 'bold',
            color: `${colors.footerIconUnSelected}`,
        },
        lessonNameForm: {
            flex: '1 1 auto',
            minWidth: 0,
            borderRadius: 1,
        },
        button: {
            color: `${colors.black}`,
            minHeight: 32,
        },
        createButton: {
            flexShrink: 0,
            backgroundColor: `${colors.footerIconSelected}`,
            fontWeight: 'bold',
            '&:hover': {
                backgroundColor: `${colors.footerIconSelected}`,
            },
            '&:focus': {
                backgroundColor: `${colors.footerIconSelected}`,
            },
            '&.Mui-focusVisible': {
                backgroundColor: `${colors.footerIconSelected}`,
            },
        },
        listWrapper: {
            width: '100%',
            maxWidth: 664,
            height: 'calc(100vh - 400px)',
            maxHeight: 'calc(100vh - 400px)',
            overflowX: 'hidden',
            overflowY: 'auto',
            margin: 'auto',
            border: `1px solid ${colors.lighterGray}`,
            backgroundColor: `${colors.white}`,
            boxSizing: 'border-box',
        },
        numberTypography: {
            fontSize: 23,
            fontWeight: 'bold',
            color: `${colors.footerIconUnSelected}`,
        },
        fillingtextForm: {
            width: '100%',
        },
        deleteButton: {
            flexShrink: 0,
            cursor: 'pointer',
            alignSelf: 'center',
        },
        finishButton: {
            flexShrink: 0,
            backgroundColor: `${colors.footerIconSelected}`,
            '&:hover': {
                backgroundColor: `${colors.footerIconSelected}`,
            },
            '&:focus': {
                backgroundColor: `${colors.footerIconSelected}`,
            },
            '&.Mui-focusVisible': {
                backgroundColor: `${colors.footerIconSelected}`,
            },
        },
        TypographyGrid: {
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
        },
        inputGrid: {
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            padding: '10px 12px',
            borderBottom: `1px solid ${colors.lighterGray}`,
        },
        inputBox: {
            flex: '1 1 auto',
            minWidth: 0,
        },
        wrapper: {
            paddingTop: 30,
        },
    }),
);

type ContainerProps = {
    visible: boolean;
};
const Container = styled.section<ContainerProps>`
    display: ${({ visible }) => (visible ? 'block' : 'none')};
    flexgrow: 1px;
    min-height: calc(100% - 148px);
`;

interface BranchItemProps {
    branch: BranchEntity;
    index: number;
    onUpdate: (branchId: number, name: string) => Promise<void>;
    onDelete: (branchId: number) => Promise<void>;
}

const BranchItem: React.FC<BranchItemProps> = ({
    branch,
    index,
    onUpdate,
    onDelete,
}) => {
    const classes = useStyles();
    const [name, setName] = useState(branch.name);

    useEffect(() => {
        setName(branch.name);
    }, [branch]);

    const handleSave = useCallback(async (event: MouseEvent<HTMLButtonElement>) => {
        const target = event.currentTarget;
        if (!name.trim()) return;
        await onUpdate(branch.id, name.trim());
        target.blur();
    }, [name, branch.id, onUpdate]);

    const handleDelete = useCallback(async () => {
        if (!confirm('拠点を削除します。よろしいですか？')) return;
        await onDelete(branch.id);
    }, [branch.id, onDelete]);

    return (
        <div className={classes.inputGrid}>
            <div className={classes.TypographyGrid}>
                <Typography variant='h3' className={classes.numberTypography}>
                    {index + 1}.
                </Typography>
            </div>
            <div className={classes.inputBox}>
                <TextField
                    className={classes.fillingtextForm}
                    variant='outlined'
                    fullWidth
                    value={name}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setName(e.target.value)
                    }
                    inputProps={{
                        style: {
                            paddingTop: 6,
                            paddingBottom: 6,
                            paddingLeft: 10,
                        },
                    }}
                />
            </div>
            <img
                className={classes.deleteButton}
                src={DeleteQuestionnaireButton}
                alt='削除'
                onClick={handleDelete}
                role='button'
            />
            <Button
                className={`${classes.button} ${classes.finishButton}`}
                variant='contained'
                onClick={handleSave}
            >
                完了
            </Button>
        </div>
    );
};

export default function BranchSettings(): React.ReactElement {
    const classes = useStyles();
    const { selected } = React.useContext(MerchantTabContext);
    const [branches, setBranches] = useState<BranchEntity[]>([]);
    const [newBranchName, setNewBranchName] = useState('');

    const visible = useMemo(() => BRANCH === selected, [selected]);

    const fetchBranches = useCallback(async () => {
        const list = await Api.fetchBranches(AuthStore.tempoId);
        setBranches(list);
    }, []);

    useEffect(() => {
        if (visible) {
            void fetchBranches();
        }
    }, [visible, fetchBranches]);

    const handleAdd = useCallback(async (event: MouseEvent<HTMLButtonElement>) => {
        const target = event.currentTarget;
        if (!newBranchName.trim()) return;
        try {
            await Api.createBranch(AuthStore.tempoId, newBranchName.trim());
            setNewBranchName('');
            await fetchBranches();
            target.blur();
            alert('拠点の追加が完了しました。');
        } catch (e) {
            alert('拠点の追加に失敗しました。');
            console.error(e);
        }
    }, [newBranchName, fetchBranches]);

    const handleUpdate = useCallback(
        async (branchId: number, name: string) => {
            try {
                await Api.updateBranch(AuthStore.tempoId, branchId, name);
                await fetchBranches();
                alert('拠点名の更新が完了しました。');
            } catch (e) {
                alert('拠点名の更新に失敗しました。');
                console.error(e);
            }
        },
        [fetchBranches],
    );

    const handleDelete = useCallback(
        async (branchId: number) => {
            try {
                await Api.deleteBranch(AuthStore.tempoId, branchId);
                await fetchBranches();
                alert('拠点名の削除が完了しました。');
            } catch (e) {
                alert('拠点の削除に失敗しました。');
                console.error(e);
            }
        },
        [fetchBranches],
    );

    if (!visible) {
        return <Fragment />;
    }

    return (
        <Container visible={visible}>
            <div className={classes.wrapper}>
                <Card className={classes.lessonName}>
                    <Typography className={classes.formTitle}>
                        拠点名:
                    </Typography>
                    <TextField
                        className={classes.lessonNameForm}
                        variant='outlined'
                        fullWidth
                        value={newBranchName}
                        onChange={(e) => setNewBranchName(e.target.value)}
                        placeholder='新規拠点名を入力'
                        inputProps={{
                            style: {
                                padding: 8,
                                borderRadius: 0,
                            },
                        }}
                    />
                    <Button
                        className={`${classes.button} ${classes.createButton}`}
                        variant='contained'
                        onClick={handleAdd}
                    >
                        完了
                    </Button>
                </Card>
                <div className={classes.listWrapper}>
                    {branches.map((branch, index) => (
                        <BranchItem
                            key={branch.id}
                            branch={branch}
                            index={index}
                            onUpdate={handleUpdate}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            </div>
        </Container>
    );
}
